/**
 * CR-7105: Read-only appeal viewer for the side that RAISED the appeal (or
 * anyone without a respond action). Before this the raiser's only affordance
 * on a submitted appeal was Cancel — the appealed field, proposed value,
 * note and evidence were invisible, so a suspected mistake meant
 * cancel-and-redo blind. Renders the same AppealDetailsContent the
 * responder's modal uses.
 */
import { X } from 'lucide-react';
import { Button } from '../../ui/button';
import { DeliveryAppeal } from '../../../lib/api';
import type { DeliveryAppealStatus } from '../../../lib/api/types/grn-types';
import AppealDetailsContent from './AppealDetailsContent';

interface ViewAppealModalProps {
  isOpen: boolean;
  onClose: () => void;
  appeal: DeliveryAppeal;
}

/** Mirror the status labelling used by the drill-down's Appeal Status column
 * (GRNScoreChartRowActions.appealStatusCell) so the modal never disagrees
 * with the row that opened it. */
function statusPill(status: DeliveryAppealStatus, isCustomerAppeal: boolean) {
  const pill = (label: string, cls: string) => (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>{label}</span>
  );
  switch (status) {
    case 'awaiting_response':
      return pill(
        isCustomerAppeal ? 'Awaiting Supplier Response' : 'Awaiting Customer Response',
        'bg-yellow-100 text-yellow-800'
      );
    case 'accepted':
      return pill('Accepted', 'bg-green-100 text-green-800');
    case 'queried':
      return pill('Query – Awaiting Supplier Response', 'bg-blue-100 text-blue-800');
    case 'rejected':
      return pill('Rejected', 'bg-red-100 text-red-800');
    case 'cancelled':
      return pill('Appeal Cancelled', 'bg-gray-200 text-gray-600');
  }
}

export default function ViewAppealModal({ isOpen, onClose, appeal }: ViewAppealModalProps) {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-bold">Appeal Details</h3>
              {statusPill(appeal.status, appeal.is_customer_appeal)}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <AppealDetailsContent appeal={appeal} />
          </div>

          {/* Footer */}
          <div className="flex justify-end p-5 border-t sticky bottom-0 bg-white">
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </>
  );
}
